/**
 * Человекочитаемые тексты ошибок API для админки.
 *
 * Сервер отдаёт ошибки в трёх формах:
 *  - baseResponse-тело `{statusCode, data: {code, details}, message}` (championship-хендлеры) —
 *    api/championships.ts#unwrap превращает его в Error с `data`;
 *  - сырой ответ preHandler'ов `{error, code}` БЕЗ `message` (авторизация, creator-гейт);
 *  - admin finance API `{error, code, message, details?}` с локализованным message.
 *
 * Здесь собраны тексты по HTTP-статусу и по известным `code`, чтобы пользователь
 * не видел «Request failed with status code 403».
 */

export interface ApiErrorInfo {
  code?: string | null
  status?: number | null
  details?: unknown
}

export interface MatchCreationFailureLike {
  matchup_id?: number | null
  championship_matchup_id?: number | null
  error?: string | null
  code?: string | null
  status?: number | null
  details?: unknown
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null

const asNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'string' && value.trim() === '' ? NaN : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const asStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => typeof item === 'string' ? item.trim() : '').filter(Boolean)
    : []

export const formatVnd = (amount: number) => `${amount.toLocaleString('ru-RU')} ₫`

/** Тексты по HTTP-статусу — последний фолбэк, когда нет ни message, ни известного code. */
export const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: 'Некорректный запрос. Проверьте введённые данные.',
  401: 'Сессия истекла. Войдите заново.',
  403: 'Нет доступа к этому действию.',
  404: 'Не найдено.',
  409: 'Конфликт состояния: действие недоступно в текущем состоянии. Обновите страницу и повторите.',
  422: 'Данные не прошли проверку. Проверьте введённые значения.',
  429: 'Слишком много запросов. Повторите чуть позже.',
  500: 'Внутренняя ошибка сервера. Повторите позже.',
  502: 'Сервер временно недоступен. Повторите позже.',
  503: 'Сервер временно недоступен. Повторите позже.',
  504: 'Сервер не ответил вовремя. Повторите позже.'
}

type CodeMessage = (details: Record<string, unknown> | null) => string

const clubConflictMatches = (details: Record<string, unknown> | null): string => {
  const conflicts = Array.isArray(details?.conflicts) ? details!.conflicts as unknown[] : []
  const ids = conflicts
    .map((item) => asNumber(asRecord(item)?.match_id))
    .filter((id): id is number => id != null)
  return ids.length ? ` Конфликтующие матчи: ${ids.map((id) => `#${id}`).join(', ')}.` : ''
}

/** Тексты по известным `code` (championship + admin finance). */
export const API_ERROR_CODE_MESSAGES: Record<string, CodeMessage> = {
  // ── Издание / чемпионат ──
  CHAMPIONSHIP_FORMAT_TEAM_COUNT_INVALID: (details) => {
    const teamCount = asNumber(details?.team_count)
    const head = teamCount != null
      ? `Формат не подходит под текущее число команд (${teamCount}).`
      : 'Формат не подходит под текущее число команд.'
    const errors = asStringList(details?.errors)
    return errors.length ? `${head} ${errors.join('; ')}` : head
  },
  CHAMPIONSHIP_NOT_APPROVED: () =>
    'Чемпионат не одобрен модерацией. Дождитесь одобрения и повторите действие.',
  CHAMPIONSHIP_VENUE_DISCOUNT_FORBIDDEN: () =>
    'Скидку на аренду может назначать только владелец стадиона или администратор. Заявите площадку без скидки.',
  CHAMPIONSHIP_VENUE_ALREADY_APPLIED: () =>
    'Эта площадка уже заявлена на издание.',
  CHAMPIONSHIP_EDITION_NOT_FOUND: () => 'Издание не найдено.',
  CHAMPIONSHIP_EDITION_CLOSED: () => 'Издание закрыто для изменений.',

  // ── Создание матчей раунда (failed[]) ──
  CHAMPIONSHIP_VENUE_TARIFF_INCOMPLETE: () =>
    'Тариф стадиона не покрывает всё время матча. Дополните тарифную сетку стадиона или выберите другое время/площадку.',
  CHAMPIONSHIP_CLUB_TIME_CONFLICT: (details) =>
    `У одного из клубов уже есть другой матч в это время.${clubConflictMatches(details)}`,
  CHAMPIONSHIP_MATCHUP_TEAMS_CHANGED: () =>
    'Состав пары изменился после расчёта расписания. Обновите страницу и повторите создание матчей.',
  CHAMPIONSHIP_VENUE_SLOT_BUSY: () => 'Слот на площадке уже занят другим матчем.',
  FIELD_PITCH_SLOT_BUSY: () => 'Слот на поле уже занят другим матчем.',
  CHAMPIONSHIP_VENUE_WORKING_HOURS: () => 'Матч выходит за рабочие часы площадки.',
  CHAMPIONSHIP_VENUE_MIN_ORDER_TIME: () => 'Время матча короче минимального времени аренды площадки.',
  CHAMPIONSHIP_VENUE_NOT_APPROVED: () => 'Площадка не одобрена для этого издания.',
  CHAMPIONSHIP_VENUE_INACTIVE: () => 'Площадка неактивна.',
  CHAMPIONSHIP_FEE_ADMISSION_NOT_READY: () =>
    'Не закрыты условия допуска по взносам. Проверьте взносы команд.',
  CHAMPIONSHIP_MATCH_START_MUST_BE_FUTURE: () => 'Время начала матча должно быть в будущем.',

  // ── Пополнение эскроу (admin finance) ──
  CHAMPIONSHIP_ESCROW_FUNDING_EXCEEDS_SHORTFALL: (details) => {
    const shortfall = asNumber(details?.prize_shortfall)
    return shortfall != null && shortfall > 0
      ? `Сумма превышает недостающую часть призового фонда. Можно внести не более ${formatVnd(shortfall)}.`
      : 'Сумма превышает недостающую часть призового фонда, либо недостачи уже нет.'
  },
  CHAMPIONSHIP_ESCROW_FUNDING_CLOSED: () =>
    'Эскроу издания закрыт для пополнения (издание отменено или призовой фонд уже закрыт).',
  CHAMPIONSHIP_PRIZE_SETTLEMENT_INVALID: () =>
    'Состояние призового settlement некорректно. Обновите страницу; если ошибка повторяется, передайте её разработчикам.',
  PRIZE_POOL_INVALID_DISTRIBUTION: () =>
    'Сохранённое распределение призового фонда некорректно. Исправьте распределение и повторите.',
  // Под этим ключом зачисление уже проведено — повтор может задвоить деньги.
  WALLET_IDEMPOTENCY_CONFLICT: () =>
    'Пополнение с этим ключом уже проведено — проверьте баланс эскроу, не повторяйте.',

  // ── Техническое поражение ──
  TECHNICAL_RESULT_CONFLICT: (details) => {
    const applied = details?.applied_no_show
    const who = applied === 'home' ? 'хозяева' : applied === 'away' ? 'гости' : applied === 'both' ? 'обе команды' : null
    return `Для этого матча уже записано другое техническое поражение${who ? ` (не явились: ${who})` : ''}.`
  },
  CHAMPIONSHIP_TECHNICAL_DOUBLE_UNSUPPORTED: () =>
    'Обоюдная неявка не поддерживается для двухматчевой пары и double elimination. Выберите одну команду.',
  TECHNICAL_MATCH_IN_PLAY: () => 'Матч идёт — сначала его должен завершить судья.',
  TECHNICAL_MATCH_MISSING: () => 'Матч этого матчапа не найден. Обновите страницу и повторите.',
  EDITION_NOT_EXECUTABLE: () =>
    'Издание сейчас не в том статусе, когда доступно это действие (нужно «Расписание» или «Идёт»).',
  TECHNICAL_MATCH_FINISHED: () => 'Матч уже сыгран — техническое поражение неприменимо.',
  TECHNICAL_MATCHUP_RESULTED: () => 'Результат этого матча уже записан.',
  TECHNICAL_TEAMS_UNRESOLVED: () => 'Обе команды матча ещё не определены.',
  TECHNICAL_FINANCE_UNSUPPORTED: () =>
    'Для этого матча техническое поражение требует ручного разбора финансов.',
  TECHNICAL_REASON_REQUIRED: () => 'Укажите причину (от 5 символов).',
  ROUND_FINISHED: () => 'Раунд уже завершён — действие недоступно.',

  // ── Судейские сборы и долги ──
  REFEREE_FEE_NOT_CLOSABLE: () =>
    'Этот судейский сбор нельзя закрыть: он уже оплачен или закрыт другим способом.',
  CHAMPIONSHIP_REFEREE_DEBT_OUTSTANDING: () =>
    'У владельца чемпионата есть непогашенный долг за судейство. Пока он не погашен, создавать чемпионаты и издания нельзя — погасите долг в разделе «Долги перед судьями» издания или через платформу.',
  REFEREE_DEBT_NOT_FOUND: () => 'Судейский долг не найден.',
  REFEREE_DEBT_NOT_DEBTOR: () => 'Погасить этот долг может только его должник.',

  // ── Отмена издания ──
  EDITION_CANCELLATION_CLOSED: () => 'В текущем статусе издание отменить нельзя.',
  EDITION_CANCELLATION_MATCH_IN_PLAY: (details) => {
    const ids = Array.isArray(details?.match_ids) ? (details!.match_ids as unknown[]).map((id) => `#${id}`).join(', ') : ''
    return `Идёт матч${ids ? ` (${ids})` : ''}. Дождитесь его завершения и повторите отмену.`
  },
  EDITION_CANCELLATION_GAMEPLAY_EXISTS: () =>
    'В несыгранном матче уже есть игровые события. Завершите его и повторите отмену.',
  EDITION_CANCELLATION_OBLIGATIONS_OPEN: () =>
    'Есть незакрытые обязательства по изданию. Закройте их и повторите отмену.',
  EDITION_CANCELLATION_MATCH_NOT_CANCELLED: () =>
    'Не все матчи издания отменены. Обновите страницу и повторите финализацию отмены.',
  EDITION_CANCELLATION_FEE_REFUND_INCOMPLETE: () =>
    'Не все взносы издания возвращены. Повторите финализацию отмены; если ошибка остаётся, передайте её разработчикам.',
  EDITION_CANCELLATION_INVALID_BALANCE: () =>
    'Некорректный баланс эскроу издания — автоматическая отмена остановлена. Передайте ошибку разработчикам.',
  EDITION_NOT_CANCELLING: () => 'Издание не находится в процессе отмены.',

  // ── Права доступа (ответы preHandler'ов) ──
  ACCESS_DENIED: () => 'Нет доступа к этому действию: недостаточно прав в этом чемпионате.',
  ADMIN_REQUIRED: () => 'Действие доступно только администраторам платформы.',
  CREATOR_REQUIRED: () => 'Действие доступно только создателю чемпионата или администратору платформы.',
  NOT_FOUND: () => 'Не найдено.'
}

/** Коды, при которых повтор с теми же параметрами заведомо бесполезен. */
const DETERMINISTIC_CODES = new Set([
  'CHAMPIONSHIP_ESCROW_FUNDING_EXCEEDS_SHORTFALL',
  'CHAMPIONSHIP_ESCROW_FUNDING_CLOSED',
  'CHAMPIONSHIP_PRIZE_SETTLEMENT_INVALID',
  'PRIZE_POOL_INVALID_DISTRIBUTION',
  'CHAMPIONSHIP_EDITION_NOT_FOUND',
  'CHAMPIONSHIP_REFEREE_DEBT_OUTSTANDING',
  'CHAMPIONSHIP_TECHNICAL_DOUBLE_UNSUPPORTED',
  'TECHNICAL_RESULT_CONFLICT',
  'REFEREE_FEE_NOT_CLOSABLE'
])

/**
 * Код, при котором идемпотентный ключ ОБЯЗАТЕЛЬНО сохраняется, хотя ответ 4xx:
 * WALLET_IDEMPOTENCY_CONFLICT значит, что под ключом уже есть проведённое зачисление.
 * Сброс ключа и повтор могут задвоить деньги.
 */
export const KEY_BOUND_CODES: ReadonlySet<string> = new Set(['WALLET_IDEMPOTENCY_CONFLICT'])

export function isKeyBoundApiError(error: unknown): boolean {
  const code = getApiErrorCode(error)
  return code != null && KEY_BOUND_CODES.has(code)
}

const GENERIC_AXIOS_MESSAGE = /^request failed(?: with status code \d+)?$/i

/** Текст по известному code; null, если code неизвестен. */
export function messageForApiErrorCode(code: string | null | undefined, details?: unknown): string | null {
  if (!code) return null
  const builder = API_ERROR_CODE_MESSAGES[code]
  return builder ? builder(asRecord(details)) : null
}

/** Текст по HTTP-статусу (только для ошибок, >= 400). */
export function messageForHttpStatus(status: number | null | undefined): string | null {
  if (typeof status !== 'number' || status < 400) return null
  if (HTTP_STATUS_MESSAGES[status]) return HTTP_STATUS_MESSAGES[status]
  return status >= 500 ? HTTP_STATUS_MESSAGES[500] : null
}

/** Фолбэк-текст: сначала по code, затем по HTTP-статусу. */
export function apiErrorMessageFor(info: ApiErrorInfo): string | null {
  return messageForApiErrorCode(info.code, info.details) ?? messageForHttpStatus(info.status)
}

export function getApiErrorStatus(error: unknown): number | null {
  const source = error as {
    status?: unknown
    statusCode?: unknown
    response?: { status?: unknown }
  } | null
  const candidates = [source?.status, source?.statusCode, source?.response?.status]
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate
  }
  return null
}

function errorPayload(error: unknown): Record<string, unknown> | null {
  const source = error as { data?: unknown; response?: { data?: unknown } } | null
  // unwrap() кладёт baseResponse.data в error.data; sanitizeApiError — тело HTTP-ответа в response.data.
  return asRecord(source?.data) ?? asRecord(source?.response?.data)
}

export function getApiErrorCode(error: unknown): string | null {
  const code = errorPayload(error)?.code
  return typeof code === 'string' && code ? code : null
}

export function getApiErrorDetails(error: unknown): Record<string, unknown> | null {
  return asRecord(errorPayload(error)?.details)
}

/**
 * Текст ошибки для message.error: message ошибки, если он осмысленный, иначе
 * текст по code/HTTP-статусу, иначе переданный фолбэк.
 */
export function describeApiError(error: unknown, fallback: string): string {
  const message = typeof (error as { message?: unknown } | null)?.message === 'string'
    ? ((error as { message: string }).message).trim()
    : ''
  if (message && !GENERIC_AXIOS_MESSAGE.test(message)) return message
  return apiErrorMessageFor({
    code: getApiErrorCode(error),
    status: getApiErrorStatus(error),
    details: getApiErrorDetails(error)
  }) || fallback
}

/**
 * Детерминированная ошибка: сервер отказал по существу (4xx, кроме 408/429) или
 * это известный «окончательный» code. Повтор с тем же телом бессмысленен, а
 * идемпотентный ключ такой попытки больше не нужен.
 */
export function isDeterministicApiError(error: unknown): boolean {
  const code = getApiErrorCode(error)
  if (code && KEY_BOUND_CODES.has(code)) return false
  if (code && DETERMINISTIC_CODES.has(code)) return true
  const status = getApiErrorStatus(error)
  return status != null && status >= 400 && status < 500 && status !== 408 && status !== 429
}

/** Описание одного сбойного матчапа из `failed[]` ответа create-matches. */
export function describeMatchCreationFailure(item: MatchCreationFailureLike): string {
  const matchupId = item.matchup_id ?? item.championship_matchup_id
  const label = matchupId != null ? `Матчап #${matchupId}` : 'Матчап'
  const known = messageForApiErrorCode(item.code, item.details)
  const serverText = typeof item.error === 'string' ? item.error.trim() : ''
  const reason = known
    ?? (serverText || messageForHttpStatus(item.status))
    ?? 'причина не указана'
  const codeSuffix = !known && item.code ? ` [${item.code}]` : ''
  return `${label}: ${reason}${codeSuffix}`
}
